import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { LogOut, Shield, Wallet, Mail, Building2 } from 'lucide-react';

export function SettingsScreen() {
  const navigate = useNavigate();
  const operator = useAuthStore(s => s.operator);
  const clear = useAuthStore(s => s.clear);

  if (!operator) return null;

  const signOut = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Settings</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Account profile and payout configuration.
        </p>
      </header>

      {/* Profile */}
      <section className="card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
            <Building2 className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-300">
            Profile
          </h2>
        </div>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Name" value={operator.name} />
          <Field label="Email" value={operator.email} />
          {operator.companyName && (
            <Field label="Company" value={operator.companyName} />
          )}
          <Field label="Account type" value={operator.accountType} />
          <Field label="Publisher key" value={operator.publisherKey} mono />
        </dl>
        <p className="mt-4 text-xs text-nurev-muted">
          To update profile fields, email{' '}
          <a
            href="mailto:contact@nurev.io"
            className="text-emerald-300 hover:underline"
          >
            contact@nurev.io
          </a>
          .
        </p>
      </section>

      {/* Payout */}
      <section className="card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
            <Wallet className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-300">
            Payout
          </h2>
        </div>
        <p className="mb-3 text-sm text-nurev-text-soft">
          Current payout rail: <strong className="text-nurev-text">XMR (Monero)</strong>{' '}
          via the multi-provider exchange router.
        </p>
        <p className="text-xs text-nurev-muted">
          Stripe Connect (USD ACH) and Circle Mint (USDC direct) are wired in
          code; both are pending NUREV's procurement of the API credentials to
          activate them. Until then, hosting payouts go in XMR. You'll receive
          an email when additional rails come online — no action required from
          you.
        </p>
      </section>

      {/* KYC */}
      <section className="card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
            <Shield className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-300">
            KYC
          </h2>
        </div>
        <p className="text-sm text-nurev-text-soft">
          Tier 1 KYC (legal entity name, business address, OFAC + PEP
          sanctions screening) is required before your first payout — but not
          before you start contributing compute.
        </p>
        <p className="mt-3 text-xs text-nurev-muted">
          When you're ready, email{' '}
          <a
            href="mailto:contact@nurev.io"
            className="text-emerald-300 hover:underline"
          >
            contact@nurev.io
          </a>{' '}
          to initiate. The lite-KYC flow runs in ~2 business days.
        </p>
      </section>

      {/* Support */}
      <section className="card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
            <Mail className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-300">
            Support
          </h2>
        </div>
        <p className="text-sm text-nurev-text-soft">
          Pilot operators have direct access to the engineering team during the
          first 90 days. Reach us at{' '}
          <a
            href="mailto:contact@nurev.io"
            className="text-emerald-300 hover:underline"
          >
            contact@nurev.io
          </a>
          {' '}— responses during US business hours.
        </p>
        <p className="mt-3 text-xs text-nurev-muted">
          Full operator runbook:{' '}
          <a
            href="https://docs.nurev.io/hosting"
            target="_blank"
            rel="noreferrer"
            className="text-emerald-300 hover:underline"
          >
            docs.nurev.io/hosting
          </a>
        </p>
      </section>

      {/* Session */}
      <section className="card">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-emerald-300">
          Session
        </h2>
        <button onClick={signOut} className="btn-secondary">
          <LogOut className="h-4 w-4" /> Sign out of this device
        </button>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
        {label}
      </dt>
      <dd
        className={`mt-1 text-sm ${
          mono ? 'font-mono text-emerald-300' : 'text-nurev-text'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
